import { MaterialCommunityIcons } from '@expo/vector-icons';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Box, Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { Modal } from 'app/design/primitives/modal';

import { useNavigationOptions } from 'app/hooks/navigation';
import type { NavigationScreen } from 'app/types/navigation';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { useCurrentProfileOffersCount, useUser } from 'app/network/user';
import { canUseOfferHub, navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
const theme = createDSTheme();

const RatingThanksScreenComponent: NavigationScreen<'rating-thanks'> = ({ navigation, route }) => {
  const {
    loanId,
    listingId,
    channelUrl,
    isLender,
    subjectImage,
    subjectName,
    recommended,
    initialExperience,
    overallRating,
    assetQualityRating,
    logisticsReliabilityRating,
    communityTrustRating,
    comment,
  } = route.params;
  const { t } = useTranslation('ratings');
  const insets = useSafeAreaInsets();
  const { data: user, isPending: isUserPending } = useUser();
  const hasOfferHubAccess = canUseOfferHub(user?.featureFlags, isUserPending);
  const { data: offersCount, isFetching: isFetchingOffersCount } = useCurrentProfileOffersCount(
    recommended && !isLender && hasOfferHubAccess,
  );
  const [isReciprocityModalOpen, setIsReciprocityModalOpen] = useState(false);

  const reciprocityShown = useRef(false);
  useEffect(() => {
    if (!reciprocityShown.current && recommended && !isLender && !isFetchingOffersCount && offersCount === 0) {
      reciprocityShown.current = true;
      setIsReciprocityModalOpen(true);
    }
  }, [isFetchingOffersCount, isLender, offersCount, recommended]);

  useNavigationOptions(navigation, {
    headerShown: false,
  });

  const handleEdit = () => {
    navigation.replace('update-rating', {
      loanId,
      listingId,
      channelUrl,
      isLender,
      subjectImage,
      subjectName,
      initialExperience,
      overallRating,
      assetQualityRating,
      logisticsReliabilityRating,
      communityTrustRating,
      comment,
    });
  };

  return (
    <Box backgroundColor="surface.default" flex={1} style={styles.screen}>
      <Column flex={1} minHeight={0} width="100%">
        <Column
          alignItems="center"
          flex={1}
          gap="spacing.general.x8"
          justifyContent="center"
          minHeight={0}
          style={[
            styles.content,
            {
              paddingTop: insets.top + theme.spacing['spacing.general.x4'],
            },
          ]}
          width="100%"
        >
          <View style={styles.hero}>
            {subjectImage ? (
              <PCImage
                aspectRatio={1}
                height={160}
                source={{ uri: subjectImage }}
                style={styles.heroImage}
                width={160}
              />
            ) : (
              <View style={[styles.heroImage, styles.heroImageFallback]}>
                <MaterialCommunityIcons color={theme.colors['text.secondary']} name="account-outline" size={64} />
              </View>
            )}

            <View style={styles.badge}>
              <Text fontSize={24} lineHeight={28} textAlign="center">
                {recommended ? '👍' : '👎'}
              </Text>
            </View>
          </View>

          <Column alignItems="center" gap="spacing.general.x4" width="100%">
            <Text color="text.primary" fontSize={36} fontWeight="700" lineHeight={43} textAlign="center">
              {t('newRating.thanks.title')}
            </Text>

            <Text color="text.primary" fontSize={24} fontWeight="500" lineHeight={29} textAlign="center">
              <Trans
                components={{
                  strong: (
                    <Text color="text.primary" fontSize={24} fontWeight="700" lineHeight={29} textAlign="center" />
                  ),
                }}
                i18nKey={recommended ? 'newRating.thanks.subtitle' : 'newRating.thanks.negativeSubtitle'}
                ns="ratings"
              />
            </Text>
          </Column>
        </Column>

        <Column style={[styles.buttonDock, { paddingBottom: insets.bottom + 40 }]} width="100%">
          <Button onPress={() => navigation.goBack()} style={styles.primaryButton} variant="plain">
            <Text color="white" fontSize={14} fontWeight="700" lineHeight={16} textAlign="center">
              {t('newRating.thanks.button')}
            </Text>
          </Button>

          <Button onPress={handleEdit} style={styles.secondaryButton} variant="plain">
            <Text fontSize={14} fontWeight="700" lineHeight={16} style={styles.secondaryButtonText} textAlign="center">
              {t('newRating.thanks.editButton')}
            </Text>
          </Button>
        </Column>
      </Column>

      <Modal
        animationType="fade"
        onClose={() => setIsReciprocityModalOpen(false)}
        open={isReciprocityModalOpen}
        transparent
      >
        <Modal.Card gap="spacing.general.x4">
          <Modal.Title allowClose onClose={() => setIsReciprocityModalOpen(false)}>
            {t('newRating.reciprocity.title')}
          </Modal.Title>
          <Text textAlign="center">{t('newRating.reciprocity.description')}</Text>
          <Button
            onPress={() => {
              setIsReciprocityModalOpen(false);
              navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
            }}
            variant="primary"
          >
            {t('newRating.reciprocity.publish')}
          </Button>
          <Button onPress={() => setIsReciprocityModalOpen(false)} variant="secondary">
            {t('newRating.reciprocity.later')}
          </Button>
        </Modal.Card>
      </Modal>
    </Box>
  );
};

export const RatingThanksScreen: NavigationScreen<'rating-thanks'> = withErrorBoundary(RatingThanksScreenComponent);

const styles = StyleSheet.create({
  badge: {
    alignItems: 'center',
    backgroundColor: '#2B8176',
    borderRadius: 1185,
    height: 47.4,
    justifyContent: 'center',
    position: 'absolute',
    right: 7.6,
    top: 114,
    width: 47.4,
  },
  buttonDock: {
    gap: 8,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
  },
  content: {
    paddingBottom: 40,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
  },
  hero: {
    height: 161,
    position: 'relative',
    width: 162,
  },
  heroImage: {
    borderColor: theme.colors.white,
    borderRadius: 500,
    borderWidth: 3,
  },
  heroImageFallback: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    justifyContent: 'center',
  },
  primaryButton: {
    alignItems: 'center',
    backgroundColor: '#1F2635',
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    padding: 10,
    width: '100%',
  },
  screen: {
    overflow: 'hidden',
  },
  secondaryButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderColor: '#1F2635',
    borderRadius: 24,
    borderWidth: 1,
    height: 48,
    justifyContent: 'center',
    padding: theme.spacing['spacing.general.x4'],
    width: '100%',
  },
  secondaryButtonText: {
    color: '#1F2635',
  },
});
